import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Mic, Upload, UploadCloud, X, Square, Play, Pause } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../../../axios';
import './deepfake.css';
import apiError from '../../../error';

const MAX_RECORDING_MS = 60 * 1000;

const formatMmSs = (ms) => {
    const totalSec = Math.floor(Math.min(ms, MAX_RECORDING_MS) / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatPlaybackMmSs = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '0:00';
    }
    const totalSec = Math.floor(seconds);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const WA_PLAYBACK_BARS = 40;

const WaVoiceNotePlayback = ({ audioSrc }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [currentSec, setCurrentSec] = useState(0);
    const [durationSec, setDurationSec] = useState(0);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || !audioSrc) {
            return undefined;
        }

        const onLoaded = () => setDurationSec(audio.duration || 0);
        const onTime = () => setCurrentSec(audio.currentTime || 0);
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        const onEnded = () => {
            setPlaying(false);
            setCurrentSec(0);
        };

        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('timeupdate', onTime);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('ended', onEnded);

        return () => {
            audio.removeEventListener('loadedmetadata', onLoaded);
            audio.removeEventListener('timeupdate', onTime);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('ended', onEnded);
        };
    }, [audioSrc]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }
        if (playing) {
            audio.pause();
        } else {
            audio.play().catch(() => { });
        }
    };

    const progressPct = durationSec > 0 ? (currentSec / durationSec) * 100 : 0;

    const barHeights = useMemo(() => (
        Array.from({ length: WA_PLAYBACK_BARS }, (_, i) => {
            const n = (Math.sin(i * 1.7) + Math.cos(i * 0.9)) * 0.5 + 0.5;
            return 5 + Math.round(n * 14);
        })
    ), []);

    return (
        <div className="wa-vn-playback">
            <audio ref={audioRef} src={audioSrc} preload="metadata" className="wa-vn-audio-el" />
            <div className="wa-vn-playback-row">
                <button
                    type="button"
                    className="wa-vn-play-btn"
                    onClick={togglePlay}
                    aria-label={playing ? 'Pause' : 'Play voice note'}
                >
                    {playing ? <Pause size={14} /> : <Play size={14} className="wa-vn-play-triangle" />}
                </button>
                <div className="wa-vn-waveform" aria-hidden>
                    {barHeights.map((h, i) => {
                        const played = ((i + 0.5) / WA_PLAYBACK_BARS) * 100 <= progressPct;
                        return (
                            <span
                                key={i}
                                className={`wa-vn-wave-bar ${played ? 'wa-vn-wave-bar--played' : ''}`}
                                style={{ height: `${h}px` }}
                            />
                        );
                    })}
                </div>
                <span className="wa-vn-time">
                    {formatPlaybackMmSs((playing || currentSec > 0.05) ? currentSec : durationSec)}
                </span>
            </div>
        </div>
    );
};

const AddNewVoiceModal = ({ show, handleClose, refreshVoices }) => {
    const [voiceName, setVoiceName] = useState('');
    const [gender, setGender] = useState('male');
    const [inputMode, setInputMode] = useState('upload');
    const [audioFile, setAudioFile] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
    const [recordingFinished, setRecordingFinished] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const recordingPreviewUrl = useMemo(() => {
        if (!recordingFinished || !audioFile) {
            return null;
        }
        return URL.createObjectURL(audioFile);
    }, [recordingFinished, audioFile]);

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const fileInputRef = useRef(null);

    const uploadLabel = useMemo(() => {
        if (audioFile?.name) {
            return audioFile.name;
        }
        return 'Drop your audio file here.';
    }, [audioFile]);

    const stopRecording = useCallback(() => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
            setIsRecording(false);
            return;
        }
        mediaRecorderRef.current.stop();
        setIsRecording(false);
    }, []);

    const startRecording = async () => {
        if (isRecording) {
            return;
        }
        setRecordingFinished(false);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm')
                ? 'audio/webm'
                : MediaRecorder.isTypeSupported('audio/mp4')
                    ? 'audio/mp4'
                    : '';
            const recorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);

            mediaRecorderRef.current = recorder;
            audioChunksRef.current = [];

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
                const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'm4a' : 'wav';
                setAudioFile(new File([blob], `voice-recording.${ext}`, { type: blob.type }));
                setRecordingFinished(true);
                audioChunksRef.current = [];
                if (recorder.stream) {
                    recorder.stream.getTracks().forEach((track) => track.stop());
                }
                mediaRecorderRef.current = null;
            };

            recorder.start();
            setIsRecording(true);
        } catch (err) {
            console.error('Microphone error:', err);
            apiError(err);
            setIsRecording(false);
        }
    };

    const handleAudioUpload = (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) {
            return;
        }
        setRecordingFinished(false);
        setAudioFile(selectedFile);
    };

    const handleRecordAgain = () => {
        setAudioFile(null);
        setRecordingFinished(false);
    };

    useEffect(() => {
        return () => {
            if (recordingPreviewUrl) {
                URL.revokeObjectURL(recordingPreviewUrl);
            }
        };
    }, [recordingPreviewUrl]);

    useEffect(() => {
        if (inputMode === 'upload' && recordingFinished) {
            setAudioFile(null);
            setRecordingFinished(false);
        }
    }, [inputMode, recordingFinished]);

    useEffect(() => {
        if (inputMode !== 'record' && isRecording) {
            stopRecording();
        }
    }, [inputMode, isRecording, stopRecording]);

    useEffect(() => {
        if (!isRecording) {
            setRecordingElapsedMs(0);
            return;
        }

        const startedAt = Date.now();
        let intervalId = null;

        const tick = () => {
            const elapsed = Date.now() - startedAt;
            setRecordingElapsedMs(Math.min(elapsed, MAX_RECORDING_MS));
            if (elapsed >= MAX_RECORDING_MS) {
                if (intervalId) {
                    clearInterval(intervalId);
                }
                stopRecording();
            }
        };

        tick();
        intervalId = setInterval(tick, 250);
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isRecording, stopRecording]);

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    const resetFormState = useCallback(() => {
        setVoiceName('');
        setGender('male');
        setInputMode('upload');
        setAudioFile(null);
        setIsRecording(false);
        setRecordingFinished(false);
        setIsSubmitting(false);
    }, []);

    const handleModalClose = () => {
        if (isSubmitting) {
            return;
        }
        if (isRecording) {
            stopRecording();
        }
        resetFormState();
        handleClose();
    };

    const handleAddVoice = async () => {
        if (!voiceName.trim()) {
            alert('Please enter a voice name');
            return;
        }
        if (!audioFile) {
            alert('Please capture or upload audio');
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append('name', voiceName.trim());
        formData.append('description', '');
        formData.append('gender', gender);
        formData.append('audio', audioFile, audioFile.name || 'recorded_audio.wav');

        try {
            await axios.post('/api/deepfake/create-voice', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            toast.success('Voice created successfully!');
            if (refreshVoices) {
                refreshVoices();
            }
            resetFormState();
            handleClose();
        } catch (err) {
            console.error('Error creating voice:', err);
            apiError(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            centered
            className="add-voice-modal"
        >
            <Modal.Header className="add-voice-modal-header">
                <div className="add-voice-modal-title-wrap">
                    <h5 className="mb-1">Add New Voice</h5>
                    <p className="mb-0">Upload a file or record audio to create a unique voice.</p>
                </div>
                <button type="button" className="add-voice-close-btn" onClick={handleModalClose} aria-label="Close modal">
                    <X size={18} />
                </button>
            </Modal.Header>

            <Modal.Body className="add-voice-modal-body">
                <div className="row">
                    <div className="col-md-6">
                        <Form.Group className="mb-0">
                            <Form.Label className="add-voice-label">Voice Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter voice name"
                                value={voiceName}
                                onChange={(e) => setVoiceName(e.target.value)}
                            />
                        </Form.Group>
                    </div>
                    <div className="col-md-6">
                        <Form.Group className="mb-0">
                            <Form.Label className="add-voice-label">Gender</Form.Label>
                            <div className="gender-radio-wrap">
                                <label className={`gender-radio-option ${gender === 'male' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="gender"
                                        checked={gender === 'male'}
                                        onChange={() => setGender('male')}
                                    />
                                    <span>Male</span>
                                </label>
                                <label className={`gender-radio-option ${gender === 'female' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="gender"
                                        checked={gender === 'female'}
                                        onChange={() => setGender('female')}
                                    />
                                    <span>Female</span>
                                </label>
                            </div>
                        </Form.Group>
                    </div>
                </div>

                <div className="mt-4">
                    <div className="source-mode-grid">
                        <label className={`source-mode-card ${inputMode === 'upload' ? 'active' : ''}`}>
                            <div className="source-mode-left">
                                <span className="source-mode-icon upload-mode-icon">
                                    <UploadCloud />
                                </span>
                                <div className="source-mode-text">
                                    <span>Upload Recording</span>
                                    <small>Browse file and upload the attachment</small>
                                </div>
                            </div>
                            <input
                                type="radio"
                                name="voice-source"
                                checked={inputMode === 'upload'}
                                onChange={() => setInputMode('upload')}
                            />
                        </label>
                        <label className={`source-mode-card ${inputMode === 'record' ? 'active' : ''}`}>
                            <div className="source-mode-left">
                                <span className="source-mode-icon upload-mode-icon">
                                    <Mic />
                                </span>
                                <div className="source-mode-text">
                                    <span>Record Now</span>
                                    <small>Instantly start the recording</small>
                                </div>
                            </div>
                            <input
                                type="radio"
                                name="voice-source"
                                checked={inputMode === 'record'}
                                onChange={() => setInputMode('record')}
                            />
                        </label>
                    </div>
                </div>

                {inputMode === 'upload' && (
                    <div className="add-voice-upload-block mt-4">
                        <input
                            ref={fileInputRef}
                            id="add-new-voice-file"
                            type="file"
                            accept="audio/*"
                            className="hidden-input"
                            onChange={handleAudioUpload}
                        />
                        <div className="add-voice-upload-label">
                            <label htmlFor="add-new-voice-file" className="add-voice-upload-drop-inner">
                                <div className="add-voice-upload-icon">
                                    <Upload />
                                </div>
                                <h6 className="mb-1">{uploadLabel}</h6>
                            </label>
                            <Button
                                type="button"
                                variant="secondary"
                                className="add-voice-upload-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Browse Files
                            </Button>
                        </div>
                    </div>
                )}

                {inputMode === 'record' && (
                    <div className="add-voice-upload-block add-voice-record-block mt-4">
                        {!isRecording && recordingFinished && audioFile ? (
                            <div className="add-voice-record-preview">
                                <div className="add-voice-preview-header">
                                    <Button
                                        type="button"
                                        variant="primary"
                                        size="sm"
                                        className="add-voice-record-again-btn"
                                        onClick={handleRecordAgain}
                                    >
                                        <Mic size={14} /> Record Again
                                    </Button>
                                </div>
                                {recordingPreviewUrl ? (
                                    <WaVoiceNotePlayback audioSrc={recordingPreviewUrl} />
                                ) : null}
                            </div>
                        ) : null}

                        {!isRecording && !recordingFinished ? (
                            <button
                                type="button"
                                className="add-voice-upload-label add-voice-record-idle"
                                onClick={startRecording}
                            >
                                <div className="add-voice-upload-icon">
                                    <Mic />
                                </div>
                                <h6 className="mb-1">Click here to start recording</h6>
                            </button>
                        ) : null}

                        {isRecording ? (
                            <div className="wa-voice-note-shell">
                                <div className='text-center'>
                                    <div className="wa-voice-note-stop-badge" onClick={stopRecording} aria-hidden>
                                        <Square size={14} />
                                    </div>
                                    <p className="wa-voice-note-meta-text">Recording...</p>
                                </div>
                                <button
                                    type="button"
                                    className="wa-voice-note-bar"
                                    aria-label="Stop recording"
                                >

                                    <div className="wa-voice-note-waves" aria-hidden>
                                        {Array.from({ length: 50 }).map((_, i) => (
                                            <span
                                                key={i}
                                                className="wa-voice-note-segment"
                                                style={{
                                                    animationDelay: `${(i % 12) * 0.08}s`,
                                                    animationDuration: `${0.35 + (i % 5) * 0.08}s`
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <span className="wa-voice-note-timer">{formatMmSs(recordingElapsedMs)}</span>

                                </button>
                            </div>
                        ) : null}

                        {!recordingFinished ? (
                            <div className="add-voice-input-sample-badge-wrap">
                                <div className="add-voice-input-sample-badge">
                                    <strong className="add-voice-input-sample-badge__label">Voice Input:</strong>
                                    {' '}
                                    This is my voice sample for creating a custom voice profile.
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="add-voice-modal-footer">
                <Button variant="secondary" onClick={handleModalClose} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    disabled={!voiceName.trim() || !audioFile || isSubmitting}
                    onClick={handleAddVoice}
                >
                    {isSubmitting ? 'Adding…' : 'Add Voice'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AddNewVoiceModal;
